import type { ThreadMeta } from '@/thread-types';
import type { ArtifactCanvasTarget, DraftCanvasTarget } from './artifacts/routes';
import type { InspectFrozenResult } from '../../src/apps/selection/host-contracts.mjs';

/** Composer-only callback. It never enters serialized workspace context. */
export type NativeSelectionAttachment = {
  capture(): ((destination: { kind: 'thread'; id: string }) => Promise<InspectFrozenResult>) | null;
};
export type CapturedNativeSelection = { freeze(): Promise<InspectFrozenResult> };

export type { WorkspaceView } from './view-definitions';
/** Knowledge tabs: the page tree with the open page, and suggestions for review. */
export type KnowledgeTab = 'pages' | 'review';
export type WorkspaceTurnContext = {
  intent: {
    mode: 'chat' | 'skill-author' | 'skill-preview' | 'skill-run';
    skillId?: string;
    revision?: number;
  };
  // Explicit conversation attachments only. Saved references belong to the selected Skill revision.
  fileIds: string[];
  sourceIds: string[];
  resultIds: string[];
  selection?: { artifactId: string; revision: number; text: string };
  /** Server-issued original selection, resolved for this actual owned Chat thread. */
  nativeSelection?: InspectFrozenResult;
  /** Captured by the selected App host; current server admission is mandatory. */
  editingApp?: import('../../src/apps/tracer/editing-base.mjs').EditingAppSelection | null;
  /** The part of this chat's App the person selected in the canvas preview: sent with the next message only. */
  appSelection?: AppSelection;
  /** The part of a store's shopping assistant pointed at with Select in the storefront canvas. */
  storefrontSelection?: StorefrontSelection;
  /** The assistant opened for this chat. Identity only; the server rechecks company access each turn. */
  storefrontTarget?: { store: string };
  /** Shopper conversations this message is about (Ask Chat on the Storefront page): Chat reads them itself. */
  storefrontConversations?: StorefrontConversations;
  /** The person opened New agent: this chat shapes one draft agent (agents/new-agent.ts). */
  newAgent?: true;
  /** Projects v1: a new chat started from a project page lives in that project (the server checks it). */
  projectId?: string;
  /** The person pressed New project: this chat shapes one draft project (draft_project). */
  newProject?: true;
  /**
   * BUILD 1, PT-008 slot: a meeting attached to a chat opened from the meeting
   * canvas, including one that is still recording. Send it only once the
   * server's turn context accepts it (`src/company-workspace/turn-context.mjs`
   * is strict and rejects unknown keys today).
   */
  meetingIds?: string[];
};
/** What Select reports about the part of an App preview the person pointed at (src/company-workspace/turn-context.mjs). */
export type AppSelection = {
  tag: string;
  /** file:line:column of the JSX element that renders it; `exact` false when it is the nearest one around it. */
  source?: string;
  exact?: boolean;
  role?: string;
  text?: string;
  landmark?: string;
};
/** A part of a store's shopping assistant, named by its CSS part (src/company-workspace/turn-context.mjs). */
export type StorefrontSelection = {
  store: string;
  part: string;
  tag: string;
  role?: string;
  text?: string;
};
/**
 * Shopper conversations a message is about, by id (src/company-workspace/turn-context.mjs): one
 * conversation, or a week of one store (or of every store, `store: null`). `label` is the chip.
 */
export type StorefrontConversations = {
  store: string | null;
  conversation?: string;
  market?: string;
  label: string;
};
export const emptyTurnContext = (): WorkspaceTurnContext => ({
  intent: { mode: 'chat' },
  fileIds: [],
  sourceIds: [],
  resultIds: [],
});
/** A new chat in a project's home. An unsent chat moving there keeps the references chosen for
 * it (its chips: files, sources, results, meetings and a Skill to use); nothing else follows. */
export function projectHomeContext(
  projectId: string,
  from?: WorkspaceTurnContext,
): WorkspaceTurnContext {
  const context = { ...emptyTurnContext(), projectId };
  if (!from) return context;
  return {
    ...context,
    fileIds: from.fileIds,
    sourceIds: from.sourceIds,
    resultIds: from.resultIds,
    ...(from.meetingIds?.length ? { meetingIds: from.meetingIds } : {}),
    ...(from.intent.mode === 'skill-run' ? { intent: from.intent } : {}),
  };
}

export interface ShareAccess {
  status?: 'pending' | 'applied';
  visibility?: 'private' | 'members' | 'company';
  audience?: { userId: string; role: 'viewer' | 'editor' }[];
  sharingRevision?: number;
  createdBy?: string | null;
  canEdit?: boolean;
  canShare?: boolean;
}
export interface CompanyFile extends ShareAccess {
  canDelete?: boolean;
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  sha256: string;
  processingStatus?: 'processing' | 'ready' | 'stored_only' | 'failed';
  failureCode?: string | null;
  includeInKnowledge: boolean;
  createdAt: string;
  // PDFs only: pages in the original; content is the extracted, page-marked text.
  pageCount?: number;
  content?: string;
  // Detail reads carry a bounded prefix of large files.
  totalCharacters?: number;
  contentTruncated?: boolean;
}
export interface CompanyMember {
  id: string;
  email: string;
  displayName: string;
  role: string;
}
export interface MeetingParticipant {
  id: string;
  userId: string | null;
  displayName: string;
  email: string | null;
  kind: 'member' | 'guest';
}

// ── Meetings (BUILD 1 contracts C-8, C-9, C-12 and PLAN-ADDENDUM A2–A4) ──────
// Mirrors GET /api/v1/company/meetings[/:id]. Server responses carry codes,
// never copy: the UI renders `speakerN{n}` etc. from ui/i18n/ns/*.

/** C-8. `capturing` only with stage `capturing`. */
export type MeetingStatus = 'capturing' | 'processing' | 'ready' | 'failed';
/** C-8. `repairing`, `summarizing` and `context` remain for legacy rows. */
export type MeetingStage =
  | 'capturing'
  | 'sealing'
  | 'transcribing'
  | 'repairing'
  | 'summarizing'
  | 'context'
  | 'ready'
  | 'failed';
export type MeetingCaptureSource = 'mic' | 'tab' | 'system';
export type MeetingTitleSource = 'default' | 'user' | 'generated';
/** Legacy single-language column; `languages` is the BUILD 1 field. */
export type MeetingLegacyLanguage = 'auto' | 'sv' | 'en';

/** The notes lane, separate from the meeting status (S6/F7). */
export type MeetingNotesStatus = 'none' | 'generating' | 'current' | 'stale' | 'failed';
export interface MeetingNotesState {
  status: MeetingNotesStatus;
  revision: number;
  failureCode: string | null;
}

/** A hole in the retained recording (C-10 seal). */
export interface MeetingCaptureGap {
  atMs: number;
  lostMs: number;
  reason: 'frames_missing' | 'interrupted';
}

/** `capture` in GET meetings/:id while capturing (WP-4's `captureSummary`). */
export interface MeetingCaptureSummary {
  startedAt: string | null;
  lastAudioAt: string | null;
  stopRequestedAt: string | null;
  /** Audio received so far, across takes. */
  elapsedMs: number;
  takes: number;
  /** The open take, or null when every take has ended. */
  activeTake: number | null;
}

/** GET meetings/capabilities (C-9). */
export interface MeetingCaptureLimits {
  maxDurationMs: number;
  frameMs: 100;
  framesPerChunk: 100;
  sampleRate: 16000;
  maxChunkBytes: number;
}
export interface MeetingCapabilities {
  record: MeetingCaptureLimits & { available: boolean; reason?: string };
  preview: { available: boolean; reason?: string };
  batch: { available: boolean; modes: ('pinned' | 'dual' | 'identify')[] };
  import: { audio: boolean; transcript: true };
  /** Transcript repair (PLAN-ADDENDUM A3, WP-8): the boundary's `repair` role. */
  repair: { available: boolean; reason?: string };
}

/** GET meetings/:id/capture (reconcile after a reload). */
export interface MeetingCaptureTake {
  take: number;
  sources: MeetingCaptureSource[];
  lastSeq: number | null;
  ended: boolean;
  /** Inclusive chunk ranges the server holds, e.g. [[0, 11], [13, 20]]. */
  storedChunks: [number, number][];
  missingChunks: number[];
}
export interface MeetingCaptureState {
  status: MeetingStatus;
  stopRequestedAt: string | null;
  takes: MeetingCaptureTake[];
}

/** A word: [startMs, endMs, text, confidence × 10 000]. */
export type MeetingWord = [startMs: number, endMs: number, text: string, confidence1e4: number];

export interface MeetingSegment {
  index: number;
  startMs: number | null;
  endMs: number | null;
  /** The effective speaker label after merges and moves. */
  speaker: string | null;
  /** A row-level person override; wins over the speaker table. */
  participantId: string | null;
  /** Editor-only; shared readers receive displayText instead. */
  rawText?: string;
  displayText: string;
  /** 0..1 */
  confidence: number | null;
  /** Sort key; a split inserts a row between two ordinals. */
  ordinal: number;
  /** The engine's label, immutable (unmerge restores it). */
  rawSpeaker: string | null;
  language: string | null;
  /** Only from GET meetings/:id/segments/:index; list and detail reads omit words. */
  words?: MeetingWord[];
  /** BUILD 2 voice pass, detail reads for the meeting's editor only. No scores. */
  voice?: { uncertain: boolean; bestOtherLabel?: string };
}

/** One voice in the speaker table (WP-6). `speakerNumber` renders as `speakerN{n}`. */
export interface MeetingSpeaker {
  label: string;
  displayOrder: number;
  speakerNumber: number;
  kind: 'unknown' | 'person' | 'not_participant';
  participantId: string | null;
  /** The resolved person name, or null (never the English "Speaker N"). */
  name: string | null;
  mergedInto: string | null;
  speechMs: number;
  segmentCount: number;
  sampleSegments: number[];
  confirmed: boolean;
  confirmedAt: string | null;
  /** BUILD 2 voice pass, for the meeting's editor only. */
  voiceSuggestions?: MeetingVoiceSuggestion[];
  voiceRecognition?: { id: string };
}
/** Every speaker write returns this; it also marks the notes stale. */
export interface MeetingSpeakerChange {
  speakers: MeetingSpeaker[];
  affectedRows: number;
  notes: MeetingNotesState;
}
export type MeetingSpeakerTarget =
  | { participantId: string | null }
  | { guest: { name: string; email?: string } }
  | { kind: 'unknown' | 'not_participant' };
export type MeetingRowSpeakerInput = { scope: 'row' | 'label' } & (
  | { participantId: string }
  | { label: string }
  | { guest: { name: string; email?: string } }
);

// ── Voice pass (BUILD 2 C2-6, C2-10) ────────────────────────────────────────
// Suggestions only: never applied without a confirmation, and never a score.
// The server half is src/company-workspace/meetings/voice/contract.mjs;
// test/voice-wire-parity.mjs pins both.

/** Orthogonal to the meeting status and the notes lane; never blocks `ready`. */
export type MeetingVoiceStatus =
  | 'none'
  | 'queued'
  | 'running'
  | 'current'
  | 'stale'
  | 'failed'
  | 'skipped';
export type MeetingVoiceSkipReason =
  | 'no_audio'
  | 'too_little_speech'
  | 'disabled'
  | 'predates_voice_pass'
  | 'transcript_only';
/** `voice` on GET meetings/:id, for the meeting's editor only. */
export interface MeetingVoiceState {
  status: MeetingVoiceStatus;
  failureCode?: string | null;
  skipReason?: MeetingVoiceSkipReason | null;
  /** The creator's per-meeting choice to suggest names from voice signatures. */
  identification: boolean;
  uncertainRows: number;
  generatedRevision: number;
}
/** A suggestion on one voice: same voice as another label, or sounds like a person. */
export interface MeetingVoiceSuggestion {
  id: string;
  kind: 'merge' | 'voiceprint' | 'identity_merge';
  otherLabel?: string;
  person?: { userId: string; displayName: string; avatarUrl?: string };
}
/** PUT meetings/:id/voice/identification. */
export type MeetingVoiceIdentificationInput = { enabled: boolean };
/** GET meetings/:id/voice/review, lowest margin first. */
export interface MeetingVoiceReview {
  rows: {
    index: number;
    label: string;
    bestOtherLabel: string;
    startMs: number | null;
    endMs: number | null;
  }[];
  total: number;
}

export interface MeetingCitation {
  text: string;
  segmentIndexes: number[];
}
/** Legacy (v1) notes: `version` is absent. */
export interface MeetingSummary {
  overview: MeetingCitation | string | null;
  decisions: MeetingCitation[];
  actionItems: (MeetingCitation & { assignee: string | null; dueDate: string | null })[];
  openQuestions: MeetingCitation[];
  knowledgeSuggestions: MeetingCitation[];
  coverage?: {
    includedSegmentCount: number;
    totalSegmentCount: number;
    partial: boolean;
  };
}
export interface MeetingActionItemV2 extends MeetingCitation {
  assignee: string | null;
  assigneeParticipantId: string | null;
  assigneeEvidence: 'spoke' | 'mentioned' | null;
  /** YYYY-MM-DD */
  dueDate: string | null;
  dueText: string | null;
}
/** C-12 notes summary v2. */
export type MeetingNotesTranscriptCoverage = NonNullable<MeetingSummary['coverage']> & {
  version: 1;
};
export interface MeetingSummaryV2 {
  version: 2;
  title?: string;
  overview: string;
  decisions: MeetingCitation[];
  actionItems: MeetingActionItemV2[];
  openQuestions: MeetingCitation[];
  risks: MeetingCitation[];
  knowledgeSuggestions: MeetingCitation[];
  meta: {
    notesRevision: number;
    model: string;
    language: string;
    speakersSnapshot: Record<string, string | null>;
    generatedAt: string;
    transcriptCoverage?: MeetingNotesTranscriptCoverage;
  };
}
export const isMeetingSummaryV2 = (
  summary: MeetingSummary | MeetingSummaryV2 | null | undefined,
): summary is MeetingSummaryV2 => !!summary && 'version' in summary && summary.version === 2;
/** Notes lists an item can come from; `renderItemActions` receives one. */
export type MeetingNoteList =
  | 'decisions'
  | 'actionItems'
  | 'openQuestions'
  | 'risks'
  | 'knowledgeSuggestions';
/** Lists that can be proposed as company knowledge (C-9). */
export type MeetingProposalList = 'decisions' | 'actionItems' | 'risks';

/** A notes item proposed as company knowledge (WP-7); `stale` when its owner changed since. */
export interface MeetingProposal {
  id: string;
  list: MeetingProposalList;
  index: number;
  status: 'pending' | 'approved' | 'rejected' | 'withdrawn';
  stale: boolean;
  notesRevision: number;
  reviewBy: string | null;
  createdAt: string;
}
export interface MeetingProposalRequestItem {
  list: MeetingProposalList;
  index: number;
  notesRevision: number;
  content?: string;
  /** YYYY-MM-DD; the server defaults to +90 days. */
  reviewBy?: string;
}

// ── Record-pass provenance and transcript repair (PLAN-ADDENDUM A2, A3; WP-5, WP-8) ──

/** One engine of the record pass. */
export interface MeetingRecordPassEngine {
  provider: string;
  model: string;
  mode: 'pinned' | 'dual' | 'identify' | 'code_switch' | null;
  languages: string[];
}
/**
 * Which engine produced the words and which the speaker turns (A2;
 * `transcript_provenance`). Paths, most to least preferred: `merged`
 * (AssemblyAI words + Transcribe turns), `words_only`, `speakers_only`, `import`.
 */
export interface MeetingTranscriptProvenance {
  version: 1;
  path: 'merged' | 'words_only' | 'speakers_only' | 'import';
  words: MeetingRecordPassEngine | null;
  speakers: (MeetingRecordPassEngine & { source: 'turns' | 'own_labels' }) | null;
  /** How each word got its speaker (merge-words-speakers.mjs). */
  merge: { words: number; byOverlap: number; byNearest: number; byFallback: number } | null;
  /** Why a less preferred path was used: a code, never provider text. */
  fallbackReason: string | null;
}
/** `repair_status` (0040). `stale`: the transcript changed after the last repair. */
export type MeetingRepairStatus =
  | 'none'
  | 'pending'
  | 'running'
  | 'current'
  | 'stale'
  | 'failed'
  | 'skipped';
/** `repair` on every meeting row. */
export interface MeetingRepairState {
  status: MeetingRepairStatus;
  failureCode: string | null;
}
/** Numbers, dates and negations are only ever suggested (enforced by a CHECK in 0040). */
export type MeetingRepairKind =
  | 'proper_noun'
  | 'term'
  | 'number'
  | 'date'
  | 'negation'
  | 'wording'
  | 'other';
/** `superseded`: a later transcript revision replaced the row the edit was made on. */
export type MeetingRepairEditStatus =
  | 'suggested'
  | 'applied'
  | 'rejected'
  | 'undone'
  | 'superseded';
/** An engine id, a fact or source id, a vocabulary term, or a row index, with optional text. */
export interface MeetingRepairEvidence {
  kind: 'engine' | 'knowledge' | 'vocabulary' | 'context';
  ref: string;
  text?: string;
}
/**
 * One structured span edit as the API returns it (`transcriptEditRecordSchema`;
 * ledger `workspace_meeting_transcript_edits`, 0040). The adjudicator's
 * `{rowId, startWord, endWord, replacement, …}` becomes `segmentIndex` and
 * `afterText`; `startWord`/`endWord` index the row's words at
 * `transcriptRevision`, end exclusive.
 */
export interface MeetingTranscriptEdit {
  id: string;
  segmentIndex: number;
  transcriptRevision: number;
  startWord: number;
  endWord: number;
  /** The raw span, kept. */
  beforeText: string;
  afterText: string;
  kind: MeetingRepairKind;
  status: MeetingRepairEditStatus;
  /** Applied without review: a term swap with confidence ≥ 0.9 that matches approved vocabulary. */
  autoApplied: boolean;
  /** 0..1 */
  confidence: number;
  /** The approved vocabulary term an automatic edit matched. */
  vocabularyTerm: string | null;
  /** Model-written, in the meeting language; untrusted display text. */
  reason: string | null;
  evidence: MeetingRepairEvidence[];
  model: string | null;
  decidedAt: string | null;
  createdAt: string;
}
/** One five-minute window of the audio and the engines that produced words in it. */
export interface MeetingCoverageWindow {
  startMs: number;
  endMs: number;
  engines: string[];
  flagged: boolean;
}
export interface MeetingTranscriptSpan {
  segmentIndex: number;
  startWord: number;
  endWord: number;
}
/** The per-meeting quality report of the automatic gates (#2819; `quality_report`). */
export interface MeetingQualityReport {
  version: 1;
  transcriptRevision: number;
  engines: { words: string; second: string | null };
  coverage: MeetingCoverageWindow[];
  /** The same phrase of ≥ 6 words appearing ≥ 3 times. */
  repeatedPhrases: { words: number; count: number; spans: MeetingTranscriptSpan[] }[];
  /** Marked, never guessed. `reason` is a code. */
  uncertainSpans: (MeetingTranscriptSpan & { reason: string })[];
  disagreements: {
    windows: number;
    adjudicated: number;
    suggested: number;
    autoApplied: number;
    unresolved: number;
  };
  flags: ('coverage_gap' | 'repeated_phrase' | 'uncertain_span')[];
  model: string | null;
  generatedAt: string;
}
/** GET meetings/:id/edits */
export interface MeetingEdits {
  edits: MeetingTranscriptEdit[];
  quality: MeetingQualityReport | null;
  repair: MeetingRepairState;
}
export type MeetingRepairAction = 'accept' | 'reject' | 'undo';
/**
 * POST meetings/:id/edits/:editId/{accept|reject|undo}. Accept and undo change
 * the row's text, so they also return the row and the (now stale) notes.
 */
export interface MeetingRepairDecision {
  edit: MeetingTranscriptEdit;
  segment?: MeetingSegment;
  notes?: MeetingNotesState;
}

// ── Live meeting rows (PLAN-ADDENDUM A4, PT-008) ────────────────────────────

/**
 * A preview `final` persisted while recording (`liveSegmentRecordSchema`;
 * `workspace_meeting_live_segments`, 0039). Provisional: the record pass
 * replaces every live row when the meeting becomes ready.
 */
export interface MeetingLiveSegment {
  /** With `seg`, unique within the meeting. */
  previewSessionId: string;
  seg: string;
  take: number;
  /** Meeting timeline: the take's offset plus `startMs`. */
  atMs: number;
  /** Take-relative times. */
  startMs: number;
  endMs: number;
  text: string;
  language: string | null;
  /** The preview provider's label, when it sends one (S9). Never a person. */
  speaker: string | null;
  provisional: true;
}
/** GET meetings/:id/live?cursor=<cursor>&limit=<1..500>, ordered by `atMs`. */
export interface MeetingLivePage {
  rows: MeetingLiveSegment[];
  /** Pass back as `cursor` to read only newer rows. */
  cursor: string | null;
}

export interface MeetingMemberNote {
  id: string;
  content: string;
  createdBy: string | null;
  author: string | null;
  source: 'manual' | 'file' | 'chat';
  fileName: string | null;
  createdAt: string;
  canDelete: boolean;
}
export interface CompanyMeeting extends Omit<ShareAccess, 'status'> {
  id: string;
  /** Null while capturing or sealing. */
  fileId: string | null;
  title: string;
  language: MeetingLegacyLanguage;
  captureMethod: 'upload' | 'browser';
  status: MeetingStatus;
  stage: MeetingStage;
  durationMs: number | null;
  canAddNotes?: boolean;
  summary: MeetingSummary | MeetingSummaryV2 | null;
  relatedContext: { text: string; sourceIds: string[] }[];
  failureCode: string | null;
  /** Null while capturing or sealing. */
  file: { name: string; mimeType: string; sizeBytes: number } | null;
  participants?: MeetingParticipant[];
  segments?: MeetingSegment[];
  corrections?: {
    id: string;
    segmentIndex: number;
    kind: string;
    beforeText: string;
    afterText: string;
    evidence: { sourceIds?: string[] };
    createdAt: string;
  }[];
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  /** Collaboration v1 metadata returned on shared meeting reads. */
  createdByName?: string | null;
  fileCreatedBy?: string | null;
  canRetry?: boolean;
  // BUILD 1 (C-9): on every list and detail row.
  /**
   * A meeting's own spoken languages, used as engine hints: one = pinned, two =
   * both (dual pin); [] = none, so the company's hints apply. Nobody picks them
   * in this client.
   */
  languages: string[];
  captureSources: MeetingCaptureSource[];
  titleSource: MeetingTitleSource;
  notes: MeetingNotesState;
  captureGaps: MeetingCaptureGap[];
  transcriptRevision: number;
  // PLAN-ADDENDUM A2/A3.
  provenance: MeetingTranscriptProvenance | null;
  repair: MeetingRepairState;
  quality: MeetingQualityReport | null;
  // BUILD 1 (C-9): detail enrichment; absent when the owning package's hook has not run.
  capture?: MeetingCaptureSummary | null;
  speakers?: MeetingSpeaker[];
  proposals?: MeetingProposal[];
  /** Open and applied edits of the current transcript revision (A3). */
  edits?: MeetingTranscriptEdit[];
  // BUILD 2 (C2-6): detail enrichment for the meeting's editor only.
  voice?: MeetingVoiceState;
}
export interface CompanySource extends ShareAccess {
  id: string;
  title: string;
  kind: 'file' | 'website' | 'company_statement';
  url: string | null;
  fileId: string | null;
  includeInKnowledge: boolean;
  contentHash: string;
  observedAt: string;
  coverage: string;
  createdAt: string;
  content?: string;
  sourceThreadId?: string | null;
  createdBy?: string | null;
  indexStatus?: 'pending' | 'indexing' | 'ready' | 'failed';
  chunkCount?: number;
  indexedChunks?: number;
}
export interface SkillSnapshot {
  revision: number;
  modelChoice: import('./model-choices.generated').ModelChoice;
  name: string;
  description: string;
  instructions: string;
  fileIds: string[];
  sourceIds: string[];
  contentHash: string;
  createdAt: string;
}
export interface CompanySkill extends ShareAccess {
  template?: { id: string; suggestedSchedule: { cron: string; timezone: string } | null } | null;
  canUnpublish?: boolean;
  canDelete?: boolean;
  id: string;
  currentRevision: number;
  publishedRevision: number | null;
  draft: SkillSnapshot;
  published: SkillSnapshot | null;
  lastPreview?: {
    id: string;
    revision: number;
    mode: string;
    status: string;
    inputHash: string;
    createdAt: string;
    completedAt: string | null;
    error: string | null;
  } | null;
  createdAt: string;
  updatedAt: string;
}
export type Cell = string | number | boolean | null;
export type TableColumnType =
  | 'text'
  | 'identifier'
  | 'number'
  | 'money'
  | 'percent'
  | 'date'
  | 'status'
  | 'link'
  | 'image'
  | 'note';
export type TableTone = 'neutral' | 'success' | 'warning' | 'danger';
export type TablePayload = {
  columns: {
    key: string;
    label: string;
    type?: TableColumnType;
    currency?: string;
    /** Decimal places for number, percent and money cells. */
    precision?: number;
    /** Change columns: positive values show a leading +; ordinary values stay unsigned. */
    signed?: boolean;
    tones?: Record<string, TableTone>;
  }[];
  rows: Record<string, Cell>[];
  rowKey?: string;
  titleKey?: string;
  idKey?: string;
  sort?: { key: string; direction: 'asc' | 'desc' };
  totals?: Record<string, string | number>;
};
export type ChartPayload = {
  type: 'bar' | 'line';
  xKey: string;
  yKeys: string[];
  rows: Record<string, Cell>[];
  /** ISO currency of the plotted values; axes and tooltips follow the money rule. */
  currency?: string;
};

/** Ephemeral visual carried by a spoken answer; it is never persisted as a result. */
export type VoicePreviewPayload =
  | {
      kind: 'table' | 'comparison';
      title: string;
      columns: string[];
      rows: string[][];
    }
  | {
      kind: 'result' | 'draft';
      title: string;
      markdown: string;
    };
// The retained Result viewer consumes the original layouts from the sole deck authority.
import type { RetainedDeckPayload as DeckPayload } from '../../src/company-workspace/data/deck-contract.mjs';
export type {
  DeckChart,
  RetainedDeckSlide as DeckSlide,
  RetainedDeckPayload as DeckPayload,
} from '../../src/company-workspace/data/deck-contract.mjs';
/** A Page (kind html): the page, its text for search, the libraries Poneva copied, and what did not carry over. */
export interface PageResultPayload {
  html: string;
  text?: string;
  resources?: { url: string; sha256: string; type: 'script' | 'style' | 'font'; bytes: number }[];
  notes?: unknown[];
  /** How many files in all did not carry over (the notes keep only the first few). */
  notCopied?: number;
}
export interface CompanyResult extends ShareAccess {
  canDelete?: boolean;
  id: string;
  title: string;
  kind: 'markdown' | 'table' | 'chart' | 'deck' | 'html';
  payload?: { markdown: string } | TablePayload | ChartPayload | DeckPayload | PageResultPayload;
  sourceIds: string[];
  threadId: string | null;
  runId: string | null;
  // Lineage of the Skill run that produced this result; null for ad-hoc work.
  skillId?: string | null;
  skillRevision?: number | null;
  runMode?: 'preview' | 'run' | null;
  createdAt: string;
  // Moves when a revision replaces the payload in place. Equal to createdAt
  // until then, so `updatedAt > createdAt` is "this has been rewritten".
  updatedAt?: string;
  // Living Results (collaboration v1): the revision people edit against, who
  // wrote it, and the agent that keeps the Result up to date, if any.
  currentRevision?: number;
  revision?: ResultRevisionLabel | null;
  maintainedBy?: ResultMaintainer | null;
}
export interface ResultRevisionLabel {
  current: number;
  authorKind: 'person' | 'assistant' | 'agent' | null;
  author: { userId: string; name: string | null } | null;
  /** The outside agent an External MCP save came from ("Claude"), else null. */
  clientName?: string | null;
  editedAt: string | null;
}
export interface ResultMaintainer {
  /** Null when the reader cannot see that agent (someone else's personal agent). */
  agentId: string | null;
  name: string | null;
  refreshedAt: string | null;
}
export interface WorkspaceData {
  files: CompanyFile[];
  sources: CompanySource[];
  skills: CompanySkill[];
  results: CompanyResult[];
}
export const emptyWorkspace = (): WorkspaceData => ({
  files: [],
  sources: [],
  skills: [],
  results: [],
});
export interface WorkspaceRun {
  id: string;
  skillId: string;
  revision: number;
  mode: string;
  status: string;
  output: string | null;
  error: string | null;
  inputHash: string;
  createdAt: string;
  completedAt: string | null;
}
export type CanvasTarget =
  | ArtifactCanvasTarget
  | DraftCanvasTarget
  | {
      kind:
        | 'app-preview'
        | 'file'
        | 'source'
        | 'skill'
        | 'result'
        | 'meeting'
        | 'agent'
        | 'action'
        | 'activity'
        // An external web source: `id` is its http(s) URL.
        | 'web'
        // An Inbox item (inbox-canvas.tsx), by its Inbox id.
        | 'inbox'
        // A pending question's picture options, larger (question-choices-canvas.tsx):
        // `id` is the ask's tool call id and `tab` the question's index.
        | 'choices'
        // A store's shopping assistant as Chat designs it (storefront/storefront-canvas.tsx), by store id.
        | 'storefront'
        // Projects v1: the project a chat is drafting (projects/draft-canvas.tsx), by the chat's thread id.
        | 'project-draft'
        // About this project, beside its home (projects/about.tsx), by project id.
        | 'project';
      id: string;
      tab?: string;
      /**
       * An App preview chosen on purpose (Back/Forward or "In this chat"): the canvas shows exactly this build and does
       * not follow the chat's newest one. Without it the preview follows the newest build in place, as always.
       */
      pinned?: boolean;
      /** An App's saved editor that Change with Chat opened: the chat's earlier builds never replace it, its later
       * ones do. */
      editor?: boolean;
      /** Message whose activity trace is shown in the object's Activity tab. */
      activityMessageId?: string;
      /** Nested activity view, persisted separately from the object's tab. */
      activityTab?: 'steps' | 'sources';
      step?: string;
      full?: boolean;
      runId?: string;
      /** Web sources only: how the answer referred to it. */
      title?: string;
      /** App previews only: the producer's own kind; absent means unknown (shown neutrally). */
      appKind?: 'app' | 'deck';
      /**
       * Skills opened from an answer only: the exact version it followed, why
       * (followed-skills.tsx) and, when found by find_right_skill, the need.
       */
      revision?: string;
      followed?: 'picked' | 'agent' | 'found' | 'read';
      need?: string;
    }
  // Knowledge pages load their own data in their feature module. A page link
  // may focus one section, an exact historical revision or one suggestion. A
  // `page-proposal` link (Inbox items, older chats) names a suggestion only;
  // it resolves to the page that suggestion belongs to.
  | {
      kind: 'page' | 'page-proposal';
      id: string;
      tab?: string;
      activityMessageId?: string;
      activityTab?: 'steps' | 'sources';
      step?: string;
      full?: boolean;
      runId?: never;
      section?: string;
      revision?: string;
      suggestion?: string;
    }
  | {
      kind: 'voice-preview';
      activityMessageId?: string;
      activityTab?: 'steps' | 'sources';
      step?: string;
      id: string;
      threadId: string;
      preview: VoicePreviewPayload;
      tab?: string;
      full?: boolean;
      runId?: never;
    }
  | {
      kind: 'knowledge';
      id?: never;
      tab?: string;
      activityMessageId?: string;
      activityTab?: 'steps' | 'sources';
      step?: string;
      full?: boolean;
      runId?: never;
    };
export interface FinderItem {
  id: string;
  kind: 'chat' | 'file' | 'skill' | 'skill_draft' | 'result' | 'knowledge_page' | 'meeting';
  title: string;
  description: string;
}
export type WorkspaceFindKind =
  | 'skill'
  | 'skill_draft'
  | 'result'
  | 'file'
  | 'chat'
  | 'knowledge_page'
  | 'meeting';
export interface WorkspaceFindHit {
  kind: WorkspaceFindKind;
  id: string;
  title: string;
  excerpt?: string;
  updatedAt?: string;
  matchReason?: string;
  /** The endpoint may provide a typed, bounded metadata projection. */
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
}
export interface WorkspaceFindPage {
  hits: WorkspaceFindHit[];
  nextOffset: number | null;
  hasMore: boolean;
  semanticStatus?: string;
  degraded?: boolean;
  coverage?: Record<string, unknown>;
}
export type WorkspaceThreads = ThreadMeta[];

export type CompanyConnectionStatus =
  | 'connected'
  | 'not_connected'
  | 'not_configured'
  | 'needs_reauthorization'
  | 'unavailable'
  | 'disabled';
export interface CompanyConnectionItem {
  mcpConnectionId?: string;
  appSetup?: {
    kind: 'meta-customer-app';
    useCase: 'ads-mcp' | 'marketing-api';
    callbackUrl: string;
    scopes: string[];
  };
  newConnections?: boolean;
  readiness?: {
    state: 'ready' | 'empty' | 'unavailable' | 'needs_reauthorization';
    reason?: string;
    toolCount?: number;
    accountCount?: number;
    checkedAt?: string;
  };
  audience: 'private' | 'company';
  id: string;
  name: string;
  description: string;
  status: CompanyConnectionStatus;
  connectionId: string | null;
  authorizationVersion?: string | null;
  accountLabel: string | null;
  connectedAt: string | null;
  lastUsedAt: string | null;
  lastError?: string | null;
  actions: {
    connect: { available: boolean };
    disconnect: { available: boolean };
    addAnother?: { available: boolean };
  };
  /** The exact connection-reads projection for this connected account, when it has reviewed reads. */
  readAccess?: import('./connection-read-access/api').ConnectionReadItem;
  /** The app's reviewed reads before an account is connected (connect-time access). */
  readCatalogue?: import('./connect-access/model').ReadCatalogue;
}
export interface CompanyConnections {
  pipedreamAvailable?: boolean;
  pipedreamSetup?: { available: boolean; reason?: string };
  pipedreamCompanySetup?: { available: boolean; reason?: string };
  companySetup: { available: boolean; reason?: string };
  status: string;
  items: CompanyConnectionItem[];
  canConnect: boolean;
  settingsPath: string;
  setup: { available: boolean; reason?: string };
}
